/**
 * Centralized Configuration
 *
 * All application configuration values in one place for easy maintenance.
 */

/**
 * API Configuration
 */
export const API_CONFIG = {
  /**
   * Backend API base URL
   * - Empty string '' = use relative paths (Vite proxy handles routing)
   * - Falls back to localhost:8002 only if VITE_API_URL is undefined
   */
  BASE_URL: import.meta.env.VITE_API_URL ?? 'http://localhost:8002',

  /**
   * LocalStorage keys for auth tokens
   */
  STORAGE_KEYS: {
    ACCESS_TOKEN: 'arceusx_auth_token',
    REFRESH_TOKEN: 'arceusx_refresh_token',
    TOKEN_EXPIRY: 'arceusx_token_expiry',
  },

  /**
   * HTTP timeouts (milliseconds)
   */
  TIMEOUTS: {
    REQUEST: 30000, // 30 seconds
  },
} as const

/**
 * Authentication Configuration
 */
export const AUTH_CONFIG = {
  /**
   * Default token expiry time in seconds
   * Used when backend doesn't return expires_in
   */
  DEFAULT_TOKEN_EXPIRY: 3600, // 1 hour

  /**
   * Routes that don't require authentication
   */
  PUBLIC_ROUTES: ['/login', '/register', '/forgot-password'],
} as const

/**
 * Cache Configuration (React Query)
 */
export const CACHE_CONFIG = {
  /**
   * How long data is considered fresh (milliseconds)
   */
  STALE_TIME: {
    METRICS: 2 * 60 * 1000, // 2 minutes
    DEALS: 2 * 60 * 1000, // 2 minutes
    PIPELINE: 2 * 60 * 1000, // 2 minutes
    FUNDS: 5 * 60 * 1000, // 5 minutes
    CONTACTS: 5 * 60 * 1000, // 5 minutes
    DEFAULT: 2 * 60 * 1000, // 2 minutes
  },

  /**
   * How long cached data stays in memory (milliseconds)
   */
  CACHE_TIME: 10 * 60 * 1000, // 10 minutes

  /**
   * Number of retry attempts for failed queries
   */
  RETRY: 1,

  /**
   * Refetch on window focus
   */
  REFETCH_ON_WINDOW_FOCUS: true,
} as const

/**
 * Pagination Configuration
 */
export const PAGINATION_CONFIG = {
  /**
   * Dashboard limits
   */
  DASHBOARD: {
    CURRENT_DEALS_PER_STAGE: 5,
    PORTFOLIO_LIMIT: 5,
    FUNDS_LIMIT: 5,
  },

  /**
   * List page default sizes
   */
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
} as const

/**
 * UI Configuration
 */
export const UI_CONFIG = {
  /**
   * Deal priority colors
   */
  PRIORITY_COLORS: {
    HIGH: 'bg-red-500',
    MEDIUM: 'bg-sieve',
    LOW: 'bg-muted-foreground/30',
  },

  /**
   * Deal stage colors (3-color system)
   */
  STAGE_COLORS: {
    ACTIVE: 'text-sieve-600', // New, Screening, Partner Meeting, Diligence
    SUCCESS: 'text-green-600', // Invested, Closed
    FAILED: 'text-red-600', // Passed
  },

  /**
   * Animation durations (milliseconds)
   */
  ANIMATION: {
    FAST: 150,
    NORMAL: 200,
    SLOW: 300,
  },
} as const

/**
 * Feature Flags
 */
export const FEATURE_FLAGS = {
  /**
   * Enable debug logging in production
   */
  ENABLE_DEBUG_LOGS: import.meta.env.DEV,

  /**
   * Enable performance monitoring
   */
  ENABLE_PERFORMANCE_MONITORING: false,

  /**
   * Enable error reporting
   */
  ENABLE_ERROR_REPORTING: false,
} as const

/**
 * Validation Rules
 */
export const VALIDATION_CONFIG = {
  /**
   * Password requirements
   */
  PASSWORD: {
    MIN_LENGTH: 6,  // Matches backend min_length=6
    MAX_LENGTH: 128,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBER: true,
    REQUIRE_SPECIAL_CHAR: false,
  },

  /**
   * Email validation
   */
  EMAIL: {
    MAX_LENGTH: 255,
  },
} as const

/**
 * Export all configs as single object for convenience
 */
export const config = {
  api: API_CONFIG,
  auth: AUTH_CONFIG,
  cache: CACHE_CONFIG,
  pagination: PAGINATION_CONFIG,
  ui: UI_CONFIG,
  features: FEATURE_FLAGS,
  validation: VALIDATION_CONFIG,
} as const

export default config
